import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiClock, FiUser, FiEdit3, FiRefreshCcw, FiDollarSign, FiCalendar, FiTag, FiCreditCard } from "react-icons/fi";
import { api } from "../SubscriptionsServices/api";
import { formatDate } from "../SubscriptionsUtils/dateFormatter";

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 }
};

const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.9,
    y: 20
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 300
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    y: 20,
    transition: {
      duration: 0.2
    }
  }
};

const getActionIcon = (action) => {
  const normalized = (action || "").toLowerCase();
  if (normalized.includes("renew")) return <FiRefreshCcw className="text-green-500" />;
  if (normalized.includes("update") || normalized.includes("edit")) return <FiEdit3 className="text-blue-500" />;
  if (normalized.includes("payment")) return <FiCreditCard className="text-purple-500" />;
  return <FiClock className="text-gray-500" />;
};

const getActionColor = (action) => {
  const normalized = (action || "").toLowerCase();
  if (normalized.includes("renew")) return "bg-green-50 border-green-200 text-green-800";
  if (normalized.includes("update") || normalized.includes("edit")) return "bg-blue-50 border-blue-200 text-blue-800";
  if (normalized.includes("payment")) return "bg-purple-50 border-purple-200 text-purple-800";
  if (normalized.includes("pause")) return "bg-yellow-50 border-yellow-200 text-yellow-800";
  if (normalized.includes("cancel")) return "bg-red-50 border-red-200 text-red-800";
  if (normalized.includes("resume")) return "bg-emerald-50 border-emerald-200 text-emerald-800";
  return "bg-gray-50 border-gray-200 text-gray-800";
};

const getRenewModeLabel = (entry) => {
  if (!entry || !entry.renewMode) return null;
  const mode = String(entry.renewMode).toLowerCase();
  if (mode === "from_today") return "from today";
  if (mode === "from_custom") return "from custom date";
  if (mode === "from_due_date") return "from last expiry date";
  return mode;
};

export default function HistoryModal({ subscription, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/subscriptions/${subscription._id}/history`);
        // Sort by date descending (most recent first)
        const sorted = (res.data || []).sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          return dateB - dateA;
        });
        setHistory(sorted);
        setError(null);
      } catch (err) {
        console.error("Error fetching history:", err);
        setError("Failed to load history");
      } finally {
        setLoading(false);
      }
    };

    if (subscription?._id) {
      fetchHistory();
    }
  }, [subscription?._id]);

  const formatHistoryDate = (dateStr) => {
    if (!dateStr) return "Unknown date";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const renderChange = (change) => {
    if (!change || typeof change !== "object") return null;
    
    const { field, label, from, to } = change;
    const displayLabel = label || field || "Field";
    const displayFrom = from !== null && from !== undefined ? String(from) : "N/A";
    const displayTo = to !== null && to !== undefined ? String(to) : "N/A";

    return (
      <div key={field || label} className="flex items-start gap-2 text-xs sm:text-sm py-1">
        <span className="text-gray-500 font-medium min-w-[100px]">{displayLabel}:</span>
        <span className="text-gray-600 line-through mr-2">{displayFrom}</span>
        <span className="text-gray-400">→</span>
        <span className="text-gray-900 font-semibold ml-2">{displayTo}</span>
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
          className="bg-gradient-to-br from-white to-gray-50 rounded-xl sm:rounded-2xl w-full max-w-[95vw] sm:max-w-[600px] max-h-[85vh] sm:max-h-[90vh] shadow-2xl border-2 border-gray-100 relative flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
                <FiClock className="text-lg" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Edit History
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                  {subscription?.name || "Subscription"}
                </p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <FiX className="text-xl" />
            </motion.button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm text-gray-600">Loading history...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-12">
                <FiClock className="mx-auto text-gray-400 text-4xl mb-3" />
                <p className="text-gray-600 text-sm">No history available</p>
              </div>
            ) : (
              <div className="space-y-4">
                {history.map((entry, idx) => {
                  const action = entry.action || "Unknown";
                  const date = formatHistoryDate(entry.date);
                  const updatedBy = entry.updatedBy || entry.renewedBy || null;
                  const updatedByEmail = entry.updatedByEmail || null;
                  const changes = entry.changes || [];
                  const renewModeLabel = getRenewModeLabel(entry);

                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className={`border-2 rounded-xl p-4 ${getActionColor(action)}`}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-2">
                          {getActionIcon(action)}
                          <div className="flex flex-col">
                            <span className="font-semibold text-sm sm:text-base">
                              {action}
                            </span>
                            {renewModeLabel && action.toLowerCase().includes("renew") && (
                              <span className="text-[11px] sm:text-xs text-gray-700 mt-0.5">
                                Mode: {renewModeLabel}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-gray-600">{date}</span>
                      </div>

                      {/* Who edited */}
                      {(updatedBy || updatedByEmail) && (
                        <div className="flex items-center gap-2 mb-3 text-xs sm:text-sm">
                          <FiUser className="text-gray-500" />
                          <span className="text-gray-700">
                            {updatedBy || updatedByEmail}
                            {updatedByEmail && updatedByEmail !== updatedBy && (
                              <span className="text-gray-500 ml-1">({updatedByEmail})</span>
                            )}
                          </span>
                        </div>
                      )}

                      {/* Changes list */}
                      {changes.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-300/50">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Changes:</p>
                          <div className="space-y-1">
                            {changes.map((change, cIdx) => (
                              <div key={cIdx} className="bg-white/60 rounded-lg p-2">
                                {renderChange(change)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Legacy fields (for backward compatibility) */}
                      {!changes.length && (
                        <>
                          {entry.previousCost !== undefined && entry.newCost !== undefined && (
                            <div className="text-xs sm:text-sm mt-2">
                              <span className="text-gray-600">Cost: </span>
                              <span className="line-through text-gray-500">{entry.costCurrency || "₹"} {entry.previousCost}</span>
                              <span className="mx-2">→</span>
                              <span className="font-semibold">{entry.costCurrency || "₹"} {entry.newCost}</span>
                            </div>
                          )}
                          {entry.previousDueDate && entry.nextDueDate && (
                            <div className="text-xs sm:text-sm mt-2">
                              <span className="text-gray-600">Due Date: </span>
                              <span className="line-through text-gray-500">{formatDate(entry.previousDueDate)}</span>
                              <span className="mx-2">→</span>
                              <span className="font-semibold">{formatDate(entry.nextDueDate)}</span>
                            </div>
                          )}
                          {entry.previousPaymentType && entry.newPaymentType && (
                            <div className="text-xs sm:text-sm mt-2">
                              <span className="text-gray-600">Payment Type: </span>
                              <span className="line-through text-gray-500">{entry.previousPaymentType}</span>
                              <span className="mx-2">→</span>
                              <span className="font-semibold">{entry.newPaymentType}</span>
                            </div>
                          )}
                        </>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Close
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
